import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, Selection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export const predictionPluginKey = new PluginKey('prediction');

// Ghost-text AI prediction, shown as a ProseMirror widget decoration at the end
// of the document. The prediction text is intentionally NOT part of the
// document: it never appears in editor.getHTML()/getText(), and because the
// show/clear transactions carry no document steps they never enter the undo
// history (this replaces draft-js's EditorState.set({allowUndo:false}) hack).
// Committing (Tab / Right-arrow) inserts the text as a normal, undoable edit,
// matching the previous behaviour where accepting a prediction produced regular
// message content.
export const PredictionExtension = Extension.create({
  name: 'prediction',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: predictionPluginKey,
        state: {
          init: () => ({ text: '' }),
          apply(tr, value) {
            const meta = tr.getMeta(predictionPluginKey);
            if (meta && typeof meta.text === 'string') {
              return { text: meta.text };
            }
            // Any real document edit clears a stale prediction.
            if (tr.docChanged) {
              return { text: '' };
            }

            return value;
          },
        },
        props: {
          decorations(state) {
            const pluginState = predictionPluginKey.getState(state);
            const text = pluginState && pluginState.text;
            if (!text) {
              return DecorationSet.empty;
            }
            // Anchor INSIDE the last text block (end of content), not at
            // doc.content.size (after the block), so the ghost renders in place.
            const pos = Selection.atEnd(state.doc).from;
            const widget = Decoration.widget(
              pos,
              () => {
                const span = document.createElement('span');
                span.className = 'prediction-ghost-text';
                span.setAttribute('data-testid', 'prediction-ghost-text');
                span.style.color = 'grey';
                span.textContent = text;

                return span;
              },
              { side: 1, ignoreSelection: true }
            );

            return DecorationSet.create(state.doc, [widget]);
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      // Show ghost prediction text (does not modify the document).
      setPredictionText:
        (text) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            dispatch(tr.setMeta(predictionPluginKey, { text: text || '' }));
          }

          return true;
        },

      // Remove ghost prediction text (does not modify the document).
      clearPredictionText:
        () =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            dispatch(tr.setMeta(predictionPluginKey, { text: '' }));
          }

          return true;
        },

      // Accept the prediction: insert it into the document at the end and clear
      // the ghost. This is a normal, undoable edit.
      commitPredictionText:
        () =>
        ({ state, tr, dispatch }) => {
          const pluginState = predictionPluginKey.getState(state);
          const text = pluginState && pluginState.text;
          if (!text) {
            return false;
          }
          if (dispatch) {
            // Insert inside the last text block (not after it as a new block).
            const end = Selection.atEnd(state.doc).from;
            tr.insertText(text, end);
            tr.setMeta(predictionPluginKey, { text: '' });
            dispatch(tr);
          }

          return true;
        },
    };
  },
});

// Helper mirroring the old checkFocusOnLastBlockEnd: prediction is only offered
// when the caret is collapsed at the very end of the document.
export const isCaretAtDocEnd = (editor) => {
  if (!editor) {
    return false;
  }
  const { selection, doc } = editor.state;

  // Collapsed caret at the end of the document's LAST block (matching the old
  // checkFocusOnLastBlockEnd). Selection.atEnd(doc).from is the end-of-content
  // position inside the last text block, so this is unaffected by how many
  // blocks precede it.
  return selection.empty && selection.$head.pos >= Selection.atEnd(doc).from;
};
